import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Paper, Box, Alert, AlertTitle } from '@mui/material';
import Header from '../components/Header';
import TranslationGrid from '../components/TranslationGrid';
import EditTranslationDialog from '../components/EditTranslationDialog';
import TranslationHeader from '../components/TranslationHeader';
import DetailsDrawer from '../components/DetailsSheet/DetailsDrawer';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { selectFetchError } from '../store/selectors/translationsSelectors';
import { openTranslationDetails } from '../store/slices/uiSlice';
import { ErrorOutline } from '@mui/icons-material';

interface LocationState {
  openDrawerId?: string;
}

const TranslationScreen: React.FC = () => {
  // Drawer state is now managed in Redux - no local state needed
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Open drawer if navigating with openDrawerId in state
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.openDrawerId) {
      dispatch(openTranslationDetails(state.openDrawerId));
    }
  }, [location.state, dispatch]);

  const translationsError = useAppSelector(selectFetchError);

  const renderErrorAlert = () => { 
    return (
        <Box sx={{ pr: 16, pl: 16}}>
          <Alert icon={<ErrorOutline />} severity="error">
            <AlertTitle>Connection error</AlertTitle>
            We are having a hard time connecting to the API. Try again later.
          </Alert>
        </Box>
      );
  }
  
  return (
    <Box sx={styles.appContainer}>
      {/* Fixed Header Section */}
      <Box sx={styles.headerSection}>
        <Header />
      </Box>

      {/* Flexible Body Section */}
      <Container maxWidth="xl" sx={styles.bodyContainer}>
        <Paper sx={styles.dataGridPaper} variant="outlined">
          {translationsError ? renderErrorAlert() :
          <><TranslationHeader /><TranslationGrid /></>}
        </Paper>
      </Container>
      <EditTranslationDialog />
      <DetailsDrawer />
    </Box>
  );
};

// Styles object
const styles = {
  appContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh', // Full viewport height
    overflow: 'hidden', // Prevent scrolling on the main container
  },
  headerSection: {
    flexShrink: 0, // Don't allow header to shrink
    zIndex: 1100, // Ensure header stays on top
  },
  bodyContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    pb: 2, // Add some padding
    overflow: 'hidden',
    height: '100%',
  },
  dataGridPaper: {
    flex: 1, // Take up remaining space in container
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: 2,
    backgroundColor: 'grey.50',
    overflow: 'hidden', // Ensure content doesn't overflow the paper
    minHeight: 0, // Allow flexbox to work properly
    justifyContent: 'center',
  },
};

export default TranslationScreen;
